import { SANITY_DOC_TYPE } from '@constants/constants.sanity'
import { toEnum } from '@lib/common-utils'
import { PAGE_HOME_FETCH_ALLResult } from '@root/src/lib/sanity/sanity-types'

type HomePageQueryResult = NonNullable<PAGE_HOME_FETCH_ALLResult>

type ExtractSectionType<T extends SectionType> = Extract<
  NonNullable<NonNullable<HomePageQueryResult['content']>[number]>,
  { type: T }
>

export type SectionType = NonNullable<NonNullable<HomePageQueryResult['content']>[number]>['type']

export type OvertakeBackdropSection = ExtractSectionType<'overtakeBackdrop'>

export type PromotionalSection = ExtractSectionType<'promotionalSection'>
export type PromotionalSectionLayout = NonNullable<PromotionalSection['layout']>

export type Content = ExtractSectionType<'contentSection'>
export type ContentSection = NonNullable<Content['sections']>[number]
export type ContentSectionType = ContentSection['type']

export type PromotionalCarrouselSection = ExtractSectionType<'promoCarrouselSection'>
export type PromotionalCarrouselSectionLayout = NonNullable<PromotionalCarrouselSection['layout']>

export type PromotionalCarrouselOfferLayout = NonNullable<
  NonNullable<NonNullable<PromotionalCarrouselSection['content']>[number]['content']>[number]['promoLayout']
>

export type RecommendationCarouselSection = ExtractSectionType<'recommendationSection'>
export type RecommendationCarouselSectionLayout = NonNullable<RecommendationCarouselSection['layout']>
export type RecommendationCarouselConfigType = NonNullable<
  NonNullable<RecommendationCarouselSection>['recommendedProducts']
>['_type']

export type RecommendationHeroSection = ExtractSectionType<'recommendationHeroSection'>
export type RecommendationHeroConfigType = NonNullable<
  NonNullable<RecommendationHeroSection>['recommendedProducts']
>['_type']

export type CountdownSection = ExtractSectionType<'countdown'>

export type SponsoredRMNBannerSection = ExtractSectionType<'sponsoredRmnBanner'>

export type ExtractContentSection<T extends ContentSectionType> = NonNullable<Extract<ContentSection, { type: T }>>

type CardSectionLayout = NonNullable<ExtractContentSection<'cardSection'>['layout']>

type ColumnContentSection = NonNullable<ExtractContentSection<'columnContent'>>

type ColumnContentSectionContent = NonNullable<ColumnContentSection['content']>[number]

export type ColumnContentLayout = NonNullable<ColumnContentSection['layout']>
export type ColumnContentChildrenType = ColumnContentSectionContent['type']

export type ColumnContentCard = Extract<ColumnContentSectionContent, { type: 'card' }>
export type ColumnContentTiledImageBanner = Extract<ColumnContentSectionContent, { type: 'tiledImageBanner' }>

export const SECTION_TYPE: {
  [k in SectionType]: k
} = {
  promotionalSection: 'promotionalSection',
  contentSection: 'contentSection',
  promoCarrouselSection: 'promoCarrouselSection',
  recommendationSection: 'recommendationSection',
  recommendationHeroSection: 'recommendationHeroSection',
  overtakeBackdrop: 'overtakeBackdrop',
  countdown: 'countdown',
  sponsoredRmnBanner: 'sponsoredRmnBanner'
} as const

export const PROMOTIONAL_SECTION_LAYOUT: {
  [k in PromotionalSectionLayout]: k
} = {
  banner: 'banner',
  bannerRona: 'bannerRona',
  promoStacked: 'promoStacked',
  multipleOffersBanner: 'multipleOffersBanner',
  promoCallout: 'promoCallout'
} as const

export const CONTENT_SECTION_TYPE: {
  [k in ContentSectionType]: k
} = {
  cardSection: 'cardSection',
  categoryCarousel: 'categoryCarousel',
  columnContent: 'columnContent'
} as const

export const PROMOTIONAL_CARROUSEL_SECTION_LAYOUT: {
  [k in PromotionalCarrouselSectionLayout]: k
} = {
  tall: 'tall',
  minimal: 'minimal'
} as const

export const PROMOTIONAL_CARROUSEL_OFFER_LAYOUT: {
  [k in PromotionalCarrouselOfferLayout]: k
} = {
  promoTag: 'promoTag',
  promoText: 'promoText'
} as const

export const RECOMMENDATION_CAROUSEL_SECTION_LAYOUT: {
  [k in RecommendationCarouselSectionLayout]: k
} = {
  highlight: 'highlight',
  minimal: 'minimal'
} as const

export const RECOMMENDATION_CAROUSEL_CONFIG_TYPE: {
  [k in RecommendationCarouselConfigType]: k
} = {
  constructorRecommendation: 'constructorRecommendation',
  criteoRecommendation: 'criteoRecommendation'
} as const

export const RECOMMENDATION_HERO_CONFIG_TYPE: {
  [k in RecommendationHeroConfigType]: k
} = {
  constructorRecommendation: 'constructorRecommendation',
  manualRecommendation: 'manualRecommendation'
} as const

export const CARD_SECTION_LAYOUT = {
  detailed: 'detailed',
  imageHighlight: 'imageHighlight',
  minimal: 'minimal',
  benefits: 'benefits'
} satisfies {
  [k in CardSectionLayout]: k
}

export const COLUMN_CONTENT_LAYOUT: {
  [k in ColumnContentLayout]: k
} = {
  default: 'default',
  tile3Mobile: 'tile3Mobile',
  tile6: 'tile6'
} as const

export const COLUMN_CONTENT_CHILDREN_TYPE: {
  [k in ColumnContentChildrenType]: k
} = {
  card: 'card',
  tiledImageBanner: 'tiledImageBanner'
}

export type NavGroupItemType = keyof typeof NAV_GROUP_ITEM_TYPE
export const NAV_GROUP_ITEM_TYPES = ['link', 'subNav'] as const
export const NAV_GROUP_ITEM_TYPE = toEnum(NAV_GROUP_ITEM_TYPES)

export type TabType = keyof typeof TAB_TYPE
export const TAB_TYPES = ['link', 'subNav'] as const
export const TAB_TYPE = toEnum(TAB_TYPES)

export type TabContentType = keyof typeof TAB_CONTENT_TYPE
export const TAB_CONTENT_TYPES = [
  SANITY_DOC_TYPE.navLinkList,
  SANITY_DOC_TYPE.navIconLink,
  SANITY_DOC_TYPE.imageBanner,
  'link'
] as const
export const TAB_CONTENT_TYPE = toEnum(TAB_CONTENT_TYPES)
